import { useEffect } from "react";
import PageHeader from "@/components/PageHeader";
import SectionHeading from "@/components/SectionHeading";
import Reveal from "@/components/Reveal";
import { Briefcase, MapPin, Mail, MessageCircle, Users, Rocket, Layers, Globe, ArrowUpRight, Sparkles } from "lucide-react";
import { COMPANY, WA_CAREERS } from "@/data/site";

const WHY_JOIN = [
  {
    icon: Rocket,
    title: "Real work from day one",
    text: "Live client projects — campaigns, shoots and launches that actually ship. No busywork.",
  },
  {
    icon: Users,
    title: "Small team, direct impact",
    text: "Work directly with the founder and senior specialists. Your work is seen, shipped and celebrated.",
  },
  {
    icon: Layers,
    title: "Learn across disciplines",
    text: "SEO, advertising, content, websites and 360° photography — exposure to the full digital craft.",
  },
  {
    icon: Globe,
    title: "Local roots, global clients",
    text: "Based in Mansa, working with clients across India, Canada, the USA, the UK and Australia.",
  },
];

const ROLES = [
  {
    slug: "seo-executive",
    title: "SEO Executive",
    type: "Full-time",
    location: "Mansa, Punjab",
    responsibilities: [
      "Keyword research, on-page and technical SEO",
      "Google Business Profile and local SEO management",
      "Rank tracking, reporting and content optimisation",
    ],
    lookingFor: ["Solid SEO fundamentals — agency experience preferred", "Comfort with Search Console and keyword research tools"],
  },
  {
    slug: "social-media-executive",
    title: "Social Media Executive",
    type: "Full-time",
    location: "Mansa, Punjab",
    responsibilities: [
      "Plan and run monthly content calendars",
      "Create posts, reels and campaign creatives",
      "Manage engagement and brand communities",
    ],
    lookingFor: ["A strong feel for Instagram & Facebook content", "Consistency, creativity and attention to detail"],
  },
  {
    slug: "content-writer",
    title: "Content Writer",
    type: "Full-time",
    location: "Mansa, Punjab",
    responsibilities: [
      "Write website copy, blogs and campaign content",
      "Shape brand voice across channels",
      "Work from SEO briefs and keyword targets",
    ],
    lookingFor: ["Clear, natural English writing", "Ability to match tone to very different businesses"],
  },
  {
    slug: "360-photographer",
    title: "360° Virtual Tour Photographer",
    type: "Full-time · Field",
    location: "Mansa + travel across Punjab",
    responsibilities: [
      "Shoot professional 360° photography on location",
      "Publish tours to Google Street View",
      "Coordinate with businesses for shoot days",
    ],
    lookingFor: ["A genuine interest in photography — training provided", "Willingness to travel for on-site shoots"],
  },
  {
    slug: "web-developer",
    title: "Web Developer",
    type: "Full-time",
    location: "Mansa, Punjab",
    responsibilities: [
      "Build fast, mobile-first business websites",
      "Maintain and improve client websites",
      "Work with the team on conversion-focused layouts",
    ],
    lookingFor: ["Strong HTML/CSS/JS fundamentals — React a plus", "Care for speed, detail and clean structure"],
  },
  {
    slug: "business-development-executive",
    title: "Business Development Executive",
    type: "Full-time",
    location: "Mansa, Punjab",
    responsibilities: [
      "Reach out to local and regional businesses",
      "Present our services and close new projects",
      "Build long-term client relationships",
    ],
    lookingFor: ["Confident communication in Punjabi, Hindi and English", "A self-driven, target-oriented mindset"],
  },
];

const applyMailto = (role) =>
  `mailto:${COMPANY.email}?cc=${COMPANY.email2}&subject=${encodeURIComponent(`Job Application — ${role}`)}`;

const Careers = () => {
  useEffect(() => {
    document.title = "Careers — Click World Online";
  }, []);

  return (
    <>
      <PageHeader
        eyebrow="Careers"
        title={<>Do work that <span className="text-electric">clicks.</span></>}
        text="Join Click World Online — a digital marketing agency in Mansa, Punjab, growing since 2017. Real clients, real projects and a team that works hands-on with everything it ships."
      />

      <section data-testid="careers-why-section" className="bg-mist text-navy dot-grid-light">
        <div className="max-w-8xl mx-auto px-6 lg:px-12 py-24 lg:py-36">
          <SectionHeading dark={false} eyebrow="Why Click World Online" title={<>Grow with a team that <span className="text-brand">ships real work.</span></>} />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
            {WHY_JOIN.map((w, i) => (
              <Reveal key={w.title} delay={0.06 * i}>
                <div data-testid={`careers-why-${i}`} className="flex gap-5 bg-white border border-navy/10 rounded-3xl p-8 h-full hover:border-brand/40 transition-colors duration-300">
                  <w.icon size={24} className="text-brand shrink-0 mt-1" />
                  <div>
                    <h3 className="font-display text-xl lg:text-2xl font-extrabold tracking-tight">{w.title}</h3>
                    <p className="mt-2 text-navy/70 leading-relaxed text-sm md:text-base">{w.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="careers-roles-section" className="bg-navy grain relative">
        <div className="max-w-8xl mx-auto px-6 lg:px-12 py-24 lg:py-36">
          <SectionHeading
            eyebrow="Open Roles"
            title={<>Current <span className="text-electric">openings.</span></>}
            text="Full-time roles based at our Mansa studio. If a role sounds like you — we'd like to hear from you."
          />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ROLES.map((r, i) => (
              <Reveal key={r.slug} delay={0.05 * i}>
                <div
                  data-testid={`careers-role-${r.slug}`}
                  className="flex flex-col bg-navy-light border border-white/10 rounded-3xl p-8 h-full hover:border-electric/40 transition-colors duration-300"
                >
                  <h3 className="font-display text-2xl font-extrabold tracking-tight text-mist">{r.title}</h3>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <span className="inline-flex items-center gap-1.5 text-xs text-mist/80 border border-white/10 bg-white/5 rounded-full px-3 py-1.5">
                      <Briefcase size={12} className="text-electric" /> {r.type}
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-xs text-mist/80 border border-white/10 bg-white/5 rounded-full px-3 py-1.5">
                      <MapPin size={12} className="text-electric" /> {r.location}
                    </span>
                  </div>
                  <div className="mt-6">
                    <p className="text-xs uppercase tracking-[0.2em] text-mist/40 font-semibold">What you'll do</p>
                    <ul className="mt-3 space-y-2 text-sm text-slatec leading-relaxed list-disc list-inside">
                      {r.responsibilities.map((x) => (
                        <li key={x}>{x}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="mt-5 flex-1">
                    <p className="text-xs uppercase tracking-[0.2em] text-mist/40 font-semibold">What we look for</p>
                    <ul className="mt-3 space-y-2 text-sm text-slatec leading-relaxed list-disc list-inside">
                      {r.lookingFor.map((x) => (
                        <li key={x}>{x}</li>
                      ))}
                    </ul>
                  </div>
                  <a
                    href={applyMailto(r.title)}
                    data-testid={`careers-apply-${r.slug}`}
                    className="mt-7 inline-flex items-center gap-2 text-electric font-semibold text-sm hover:gap-3 transition-[gap] duration-300"
                  >
                    Apply for this role <ArrowUpRight size={16} />
                  </a>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-12">
            <div
              data-testid="careers-open-callout"
              className="flex flex-col md:flex-row md:items-center gap-5 bg-navy-light/60 border border-white/10 rounded-3xl p-8"
            >
              <Sparkles size={26} className="text-electric shrink-0" />
              <p className="text-slatec leading-relaxed text-sm md:text-base">
                <span className="text-mist font-semibold">Don't see your role?</span> We're always glad to meet talented people — writers,
                designers, editors, marketers. Send us your CV anyway and tell us how you'd make the team stronger.
              </p>
              <a
                href={applyMailto("Open Application")}
                data-testid="careers-open-apply"
                className="md:ml-auto inline-flex items-center gap-2 text-electric font-semibold text-sm shrink-0 hover:gap-3 transition-[gap] duration-300"
              >
                Send an open application <ArrowUpRight size={16} />
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="careers-how-to-apply-section" className="bg-navy-deep grain relative">
        <div className="max-w-8xl mx-auto px-6 lg:px-12 py-24 lg:py-36">
          <SectionHeading
            eyebrow="How To Apply"
            title={<>Three steps. <span className="text-electric">No portals.</span></>}
            text="We keep hiring as straightforward as our work — no forms, no tracking systems, just a direct conversation."
          />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
            <Reveal>
              <div data-testid="careers-step-email" className="relative bg-navy-light border border-white/10 rounded-3xl p-8 h-full overflow-hidden">
                <span className="font-display font-black text-6xl text-outline absolute top-6 right-8" aria-hidden="true">01</span>
                <Mail size={26} className="text-electric" />
                <h3 className="mt-6 font-display text-xl font-extrabold tracking-tight text-mist">Email your CV</h3>
                <p className="mt-3 text-slatec leading-relaxed text-sm">
                  Send your CV and a short introduction to {COMPANY.email} (CC {COMPANY.email2}) with the role in the subject line.
                </p>
                <a
                  href={applyMailto("Application")}
                  data-testid="careers-email-button"
                  className="mt-6 inline-flex items-center gap-2 bg-brand hover:bg-electric hover:text-navy-deep text-white text-sm font-semibold px-6 py-3 rounded-full transition-colors duration-300"
                >
                  Email Your CV <ArrowUpRight size={15} />
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.08}>
              <div data-testid="careers-step-whatsapp" className="relative bg-navy-light border border-white/10 rounded-3xl p-8 h-full overflow-hidden">
                <span className="font-display font-black text-6xl text-outline absolute top-6 right-8" aria-hidden="true">02</span>
                <MessageCircle size={26} className="text-electric" />
                <h3 className="mt-6 font-display text-xl font-extrabold tracking-tight text-mist">Or WhatsApp us</h3>
                <p className="mt-3 text-slatec leading-relaxed text-sm">
                  Prefer chatting first? Message us on WhatsApp — tell us which role you're interested in and share your CV.
                </p>
                <a
                  href={WA_CAREERS}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="careers-whatsapp-button"
                  className="mt-6 inline-flex items-center gap-2 border border-white/20 text-mist hover:border-electric hover:text-electric text-sm font-semibold px-6 py-3 rounded-full transition-colors duration-300"
                >
                  WhatsApp About Jobs <ArrowUpRight size={15} />
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.16}>
              <div data-testid="careers-step-response" className="relative bg-navy-light border border-white/10 rounded-3xl p-8 h-full overflow-hidden">
                <span className="font-display font-black text-6xl text-outline absolute top-6 right-8" aria-hidden="true">03</span>
                <Users size={26} className="text-electric" />
                <h3 className="mt-6 font-display text-xl font-extrabold tracking-tight text-mist">We get back to you</h3>
                <p className="mt-3 text-slatec leading-relaxed text-sm">
                  Every application is read by a person. Shortlisted candidates hear back within a few working days for a conversation —
                  in person or over a call.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
};

export default Careers;
